/* ============================================================================
 * ЭКРАН АУДИТОРА (роль auditor, задача №13)
 *
 * Аудитору закрыты почти все маршруты: /api/declarations и /api/geo дают 403,
 * инциденты и доверие открыты. Раньше ему показывался тот же экран, что и
 * диспетчеру, с вкладкой «БВС» (управление дронами) и кнопками согласования —
 * то есть интерфейс предлагал то, на что он получит отказ.
 *
 * Здесь только то, что аудитор реально может увидеть:
 *   - состояние подсистемы доверия (аномалии, антиповтор, отказы авторизации);
 *   - события и команды, прошедшие через контур доверия;
 *   - счётчик инцидентов и принятых/отклонённых команд.
 *
 * Данные — только те, что сервер реально отдаёт этой роли. Ничего не выдумываем:
 * отсутствующее показываем как «нет данных», а не как ноль событий.
 * ========================================================================= */

import { ShieldCheck, TriangleAlert, Ban, Radio, ScrollText } from 'lucide-react'

import { EmptyHint, Metric, Panel } from '../ui/Panels'
import { StateBadge, StateDot } from '../ui/State'
import { normalizeDeclarationStatus } from '../../lib/api'
import { DECLARATION_STATUS_LABEL } from '../../lib/statusModel'
import type { AsyncState, FlightDeclaration, TrustSnapshot } from '../../types'

export function AuditorScreen({
  trust,
  declarations,
}: {
  trust: AsyncState<TrustSnapshot>
  declarations: AsyncState<FlightDeclaration[]>
}) {
  const snap = trust.data
  const alerts = snap?.alerts ?? []
  const commands = Array.isArray(snap?.commands) ? snap!.commands : []
  const replay = snap?.anti_replay ?? {}
  const cross = snap?.cross_domain ?? {}

  if (!snap) {
    return (
      <EmptyHint>
        <span className="flex flex-col items-center gap-1.5">
          <ShieldCheck size={20} className="text-ink-muted" aria-hidden />
          Данные аудита недоступны
          <span className="max-w-[280px]">
            {trust.status === 'loading'
              ? 'Запрос /api/trust…'
              : 'Подсистема доверия не отвечает. Проверьте консоль ЦУП на :8105.'}
          </span>
        </span>
      </EmptyHint>
    )
  }

  const criticalAlerts = alerts.filter((a) => {
    const sev = String((a as Record<string, unknown>).severity ?? '').toLowerCase()
    return sev === 'critical' || sev === 'high'
  })

  return (
    <div className="flex flex-col gap-2 p-2">
      {/* --------- СВОДКА --------- */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric
          label="Аномалии"
          value={String(alerts.length)}
          state={criticalAlerts.length > 0 ? 'warning' : 'active'}
        />
        <Metric
          label="Отклонено повторов"
          value={String(replay.replay_rejected ?? 0)}
          state={replay.replay_rejected ? 'warning' : 'active'}
        />
        <Metric label="Команд принято" value={String(snap.commands_accepted ?? 0)} state="active" />
        <Metric label="Отказов авторизации" value={String(snap.auth_fail ?? 0)} state={snap.auth_fail ? 'warning' : 'active'} />
      </div>

      {/* --------- СОСТОЯНИЕ КОНТУРА ДОВЕРИЯ --------- */}
      <Panel title="Контур доверия" icon={ShieldCheck}>
        <div className="flex flex-col gap-1.5 text-[12px]">
          <Row label="Всего событий" value={String(alerts.length)} />
          <Row
            label="Критических"
            value={String(criticalAlerts.length)}
            state={criticalAlerts.length ? 'warning' : 'active'}
          />
          <Row label="Принято подписей" value={String(replay.accepted ?? 0)} />
          <Row
            label="Отклонено повторов"
            value={String(replay.replay_rejected ?? 0)}
            state={replay.replay_rejected ? 'warning' : 'active'}
          />
          <Row label="Устаревших" value={String(replay.stale_rejected ?? 0)} />
          <Row label="Узлов в контуре" value={String(replay.nodes ?? 0)} />
          <Row label="Активов с заявкой" value={String(snap.intent_assets ?? 0)} />
          <Row label="Записей конфликтов" value={String(cross.conflict_log_size ?? 0)} />
          <Row label="Судов в зоне" value={String(cross.vessels?.length ?? snap.vessels?.length ?? 0)} />
        </div>
      </Panel>

      {/* --------- АНОМАЛИИ --------- */}
      <Panel
        title="Аномалии"
        icon={TriangleAlert}
        state={<StateBadge state={alerts.length ? 'warning' : 'active'}>{alerts.length ? 'ЕСТЬ' : 'ЧИСТО'}</StateBadge>}
      >
        {alerts.length === 0 ? (
          <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-ink-muted">
            <StateDot state="active" />
            Аномалий не зафиксировано
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {alerts.slice(0, 50).map((a, i) => {
              const rec = a as Record<string, unknown>
              return (
                <div key={String(rec.id ?? i)} className="rounded border border-line px-2 py-1.5 text-[11px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{String(rec.kind ?? rec.type ?? 'событие')}</span>
                    <span className="text-ink-muted">{String(rec.at ?? rec.ts ?? '')}</span>
                  </div>
                  <div className="text-ink-muted">
                    {String(rec.detail ?? rec.text ?? rec.reason ?? '—')}
                  </div>
                </div>
              )
            })}
            {alerts.length > 50 && (
              <div className="px-2 py-1 text-[11px] text-ink-muted">
                показаны первые 50 из {alerts.length} — полный список у ЦУП
              </div>
            )}
          </div>
        )}
      </Panel>

      {/* --------- КОМАНДЫ В КОНТУРЕ --------- */}
      <Panel
        title="Команды в контуре доверия"
        icon={Radio}
        state={<StateBadge state={commands.length ? 'pending' : 'active'}>{commands.length}</StateBadge>}
      >
        {commands.length === 0 ? (
          <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-ink-muted">
            <Ban size={14} aria-hidden />
            Команд в текущем окне нет
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {commands.slice(0, 50).map((c, i) => {
              const rec = c as Record<string, unknown>
              return (
                <div key={i} className="rounded border border-line px-2 py-1.5 text-[11px]">
                  <span className="font-mono">{String(rec.cmd ?? rec.command ?? 'команда')}</span>
                  {rec.target !== undefined && (
                    <span className="text-ink-muted"> → {String(rec.target)}</span>
                  )}
                  {rec.at !== undefined && (
                    <span className="text-ink-muted"> · {String(rec.at)}</span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Panel>

      {/* --------- ЖУРНАЛ ЗАЯВОК (только чтение) --------- */}
      <Panel
        title="Журнал заявок (только чтение)"
        icon={ScrollText}
        state={<StateBadge state="pending">Согласование недоступно</StateBadge>}
      >
        {declarations.status === 'ok' ? (
          <div className="flex flex-col gap-1 text-[11px]">
            {(declarations.data ?? []).slice(0, 25).map((d, i) => {
              // Статус приводим к канону тем же разбором, что и основной список:
              // у FlightDeclaration нет полей state_label/status_raw-строковых, они
              // приходят из normalizeDeclarationStatus.
              const parsed = normalizeDeclarationStatus(d)
              return (
                <div key={d.id ?? i} className="flex justify-between gap-2 rounded border border-line px-2 py-1">
                  <span className="truncate">
                    {String(d.pilot ?? d.operator ?? `Заявка ${i + 1}`)}
                  </span>
                  <span className="shrink-0 text-ink-muted">
                    {DECLARATION_STATUS_LABEL[parsed.status]}
                  </span>
                </div>
              )
            })}
            {(declarations.data ?? []).length === 0 && (
              <div className="px-2 py-2 text-ink-muted">Заявок нет</div>
            )}
          </div>
        ) : (
          <div className="px-2 py-2 text-[11px] text-ink-muted">
            Список заявок роли аудитора недоступен (GET /api/declarations → 403 по
            правилам ivo_auth.RULES). Это ожидаемо: аудитор не согласует и не
            создаёт заявки.
          </div>
        )}
      </Panel>
    </div>
  )
}

function Row({ label, value, state }: { label: string; value: string; state?: 'active' | 'warning' | 'pending' }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded border border-line px-2 py-1">
      <span className="text-ink-muted">{label}</span>
      <span className="flex items-center gap-1.5 font-semibold">
        {state && <StateDot state={state} />}
        {value}
      </span>
    </div>
  )
}